// File: src/pages/Checkout.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl, stripePublicKey } from "../../Environments/environment";
import { toast } from "react-toastify";

const stripePromise = loadStripe(stripePublicKey);

/**
 * CheckoutContent renders inside Elements when clientSecret is available
 */
function CheckoutContent({ amountDisplay }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      // CASE 1: UI/frame/validation errors → don't navigate
      if (result.error) {
        console.error("Stripe UI error:", result.error);
        toast.error(result.error.message);
        setLoading(false);
        return;
      }

      // CASE 2: Payment succeeds
      navigate("/dashboard/success");
    } catch (err) {
      // CASE 3: Critical system crash (Rare)
      console.error("Unexpected error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto p-6 border rounded-md bg-white"
    >
      <div className="mb-4 text-sm text-gray-600">Enter payment details</div>
      <div style={{ minWidth: 400 }}>
        <PaymentElement />
      </div>
      <button
        type="submit"
        disabled={!stripe || loading}
        className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-md"
      >
        {loading ? "Processing..." : `Pay ${amountDisplay} & Save Card`}
      </button>
    </form>
  );
}

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState("");
  const amountParam = Number(searchParams.get("amount")) || 0;
  const signParam = String(searchParams.get("sign")) || "";

  console.log(amountParam, signParam);
  const amountDisplay = `$${(amountParam / 100).toFixed(2)}`;
  const { selectedPropertyId } = useProperty();
  const propertyId = selectedPropertyId;

  const checkSign = async (amountParam, signParam) => {
    if (amountParam < 9000) {
      toast.error("Minimum purchase is 500 credits.");
      return;
    }

    try {
      await apiClient.get(
        `${baseUrl}/stripe/verify-sign?amount=${amountParam}&sign=${signParam}`
      );
      createIntent();
    } catch (error) {
      console.error(error);

      const errorMessage =
        error.response?.data?.error?.message || "Error getting payment data.";
      toast.error(errorMessage);
    } finally {
    }
  };
  const createIntent = async () => {
    try {
      // You'll need the customerId — fetch wallet
      const response = await apiClient.get(
        `${baseUrl}/stripe/wallet/${propertyId}`
      );
      const wallet = response.data.result;
      const customerId = wallet?.stripeCustomerId;
      if (!customerId) {
        toast.error("No Stripe customer found. Create a customer first.");
        navigate("/dashboard/add-credits");
        return;
      }
      const response2 = await apiClient.post(
        `${baseUrl}/stripe/create-payment-intent`,
        { customerId, propertyId, userId: wallet._id, amount: amountParam }
      );
      const { clientSecret } = response2.data.result;
      setClientSecret(clientSecret);
    } catch (err) {
      console.error("Failed creating payment intent:", err);
      navigate("/dashboard/payment-failed", {
        state: { error: err.message },
      });
    }
  };
  useEffect(() => {
    console.log(amountParam, signParam);
    if (signParam) {
      checkSign(amountParam, signParam);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountParam, signParam]);

  if (!clientSecret) {
    return (
      <div className="p-6">
        <div>Preparing payment...</div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: { theme: "stripe" },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">Checkout</h2>
        <CheckoutContent amountDisplay={amountDisplay} />
      </div>
    </Elements>
  );
}
